import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { request } from "@/shared/http/client";
import type { AccessModule } from "./modules";

/**
 * QUEM ALCANÇA O QUÊ — pessoas e agentes, uma pergunta só.
 *
 * ── POR QUE ISTO MORA EM `shared/` ────────────────────────────────────────
 *
 * Porque cinco telas fazem a mesma pergunta sobre coisas diferentes — Acessos,
 * Equipe, Canais, Funis e a tela do agente — e uma feature nunca importa outra
 * (`no-cross-feature`, severidade erro). Duas cópias de um contrato de permissão
 * é como uma tela continua a mandar `code` depois de a outra ter aprendido a
 * mandar id.
 *
 * ── A REGRA, PORQUE ELA LÊ AO CONTRÁRIO ───────────────────────────────────
 *
 * `narrowed` é a metade que não se infere das concessões. Um ator SEM
 * estreitamento num tipo alcança TUDO desse tipo; um ator estreitado e sem
 * concessão nenhuma alcança NADA dele. As duas coisas seriam a mesma lista vazia
 * se `narrowed` não existisse — e era exatamente essa a armadilha que as tabelas
 * antigas liam ao contrário uma da outra. Tudo o que desenhar isto tem de dizer
 * qual das duas é, em palavras, ou o primeiro admin a abrir o painel marca-se a
 * si "por garantia" e tranca a equipa de fora.
 */
export const RESOURCE_KINDS = ["entity", "channel", "pipeline", "screen"] as const;
export type ResourceKind = (typeof RESOURCE_KINDS)[number];

export type Verb = "read" | "write";
export type Role = "member" | "admin" | "owner";

export interface AccessGrant {
	readonly kind: ResourceKind;
	readonly key: string;
	readonly verb: Verb;
}

export interface Reach {
	readonly narrowed: readonly ResourceKind[];
	readonly grants: readonly AccessGrant[];
}

export interface AccessPerson {
	readonly id: string;
	readonly name: string;
	readonly role: Role;
	/** Alcança o Ben do painel. NÃO afeta o copiloto dentro da conversa. */
	readonly benPanel: boolean;
	readonly disabledModules: readonly AccessModule[];
	/** O alcance INDIVIDUAL. Com perfil fica adormecido (não conta) e volta quando o perfil sai. */
	readonly reach: Reach;
	/** O perfil de acesso desta pessoa; ausente/nulo = só o individual. */
	readonly profileId?: string | null;
	/** "Só desta pessoa" além do perfil — guardado à parte, só conta com perfil. */
	readonly extras?: Reach;
	/** Tabelas com "só os dele" em que esta pessoa vê todos os registros (individual). */
	readonly seesAll?: readonly string[];
	/** Extras de quem tem perfil: "vê todos" e módulos ligados só para ela. */
	readonly extraSeesAll?: readonly string[];
	readonly extraModules?: readonly AccessModule[];
	readonly extraBenPanel?: boolean;
}

/** Um perfil de acesso: um alcance com nome, partilhado por quem o recebe. */
export interface AccessProfile {
	readonly id: string;
	readonly name: string;
	readonly reach: Reach;
	readonly seesAll: readonly string[];
	/** Os módulos que o perfil desliga e se dá o Ben do painel. */
	readonly disabledModules?: readonly AccessModule[];
	readonly benPanel?: boolean;
	/** Quem tem o perfil e está suspenso (fora da lista de pessoas). */
	readonly suspendedHolders?: number;
}

export interface AccessAgent {
	readonly code: string;
	readonly name: string;
	readonly role: Role;
	readonly reach: Reach;
}

export const accessKeys = {
	all: () => ["access"] as const,
	resource: (kind: ResourceKind, key: string) => ["access", "resource", kind, key] as const,
};

/**
 * O que salvar um alcance TORNA DESATUALIZADO.
 *
 * Mudar quem alcança um canal muda que conversas voltam; mudar uma entidade muda
 * o próprio MENU, que nasce da lista de entidades do servidor. Nomeado aqui e não
 * passado por cada chamador: no dia em que houver um quinto tipo, a tela que
 * esquecer esta linha é a que mostra uma lista velha e nunca diz porquê.
 */
const NARROWS: Record<ResourceKind, readonly string[]> = {
	channel: ["inbox", "conversations"],
	pipeline: ["board"],
	entity: ["structure", "records"],
	screen: ["structure", "component-screens"],
};

/**
 * O QUE EU ALCANÇO — a capacidade de quem está a olhar.
 *
 * Hoje uma só: o Ben do painel. Lida do servidor e não da sessão, porque um
 * interruptor cujo propósito é fechar uma porta não pode ficar aberto durante o
 * tempo de um cache — e o papel, que ESTÁ na sessão, está lá por custo de login e
 * não por ser a forma certa.
 *
 * O DEFEITO aberto é `true`: enquanto a resposta não chega, o orbe aparece. É
 * deliberado e é o lado certo de estar errado — esconder o Ben de quem o tem, a
 * cada recarga de página, seria um defeito que toda gente vê; mostrá-lo por um
 * instante a quem não o tem custa um clique que a ROTA recusa com uma frase.
 */
/**
 * `enabled`: só a moldura o passa. Ela monta esta consulta na raiz, ANTES de
 * saber se a rota é `bare` — e numa página pública embutida num site em sandbox
 * (origem opaca) o pedido saía com cookie, levava 401 e um erro de CORS na
 * consola que o inspetor do Ben lia como defeito do site do tenant (29/09).
 * Quem não tem sessão não tem acesso a perguntar.
 */
export function useMyAccess(enabled?: () => boolean) {
	return useQuery({
		queryKey: ["access", "me"] as const,
		queryFn: async (): Promise<{ benPanel: boolean; disabledModules: readonly AccessModule[] }> =>
			request("/v3/identity/access/me"),
		staleTime: 30_000,
		enabled: () => enabled?.() ?? true,
	});
}

export function useAccessDirectory() {
	return useQuery({
		queryKey: accessKeys.all(),
		queryFn: async (): Promise<{
			people: AccessPerson[];
			agents: AccessAgent[];
			profiles?: AccessProfile[];
		}> => request("/v3/identity/access"),
	});
}

/**
 * QUEM ALCANÇA ESTE RECURSO — a pergunta lida de trás para a frente.
 *
 * Responde apenas com os atores ESTREITADOS que o têm concedido. Quem não está
 * estreitado alcança-o sem aparecer aqui, e é por isso que nenhuma tela desenha
 * esta lista sozinha: sem a frase que diz o que a ausência significa, uma lista
 * vazia lê-se como "ninguém vê" quando quer dizer "ninguém está limitado".
 */
export function useActorsGranted(kind: ResourceKind, key: () => string | null) {
	return useQuery({
		queryKey: ["access", "resource", kind, key] as const,
		queryFn: async (): Promise<Array<{ actorKind: "user" | "agent"; actorId: string }>> => {
			const params = new URLSearchParams({ kind, key: key() ?? "" });
			const res = await request<{
				actors: Array<{ actorKind: "user" | "agent"; actorId: string }>;
			}>(`/v3/identity/access/resource?${params}`);
			return res.actors;
		},
		enabled: () => !!key(),
	});
}

/**
 * PUT, e o corpo é o alcance INTEIRO — porque a lista É o facto.
 *
 * O que volta é o que ficou GRAVADO, não o que foi pedido: um código que não
 * existe é descartado pelo servidor. A tela desenha o que voltou em vez de assumir
 * — uma concessão que ficou no ecrã e não está na tabela é o tipo de erro que
 * ninguém vai procurar.
 */
export function useSetReach() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (input: {
			actorKind: "user" | "agent" | "profile" | "extra";
			actorId: string;
			narrowed: readonly ResourceKind[];
			grants: readonly AccessGrant[];
		}): Promise<Reach> => {
			const res = await request<{ reach: Reach }>(
				`/v3/identity/access/${input.actorKind}/${encodeURIComponent(input.actorId)}`,
				{
					method: "PUT",
					body: { narrowed: [...input.narrowed], grants: [...input.grants] },
				},
			);
			return res.reach;
		},
		onSuccess: (_reach, input) => {
			void client.invalidateQueries({ queryKey: accessKeys.all() });
			for (const kind of input.narrowed) {
				for (const affected of NARROWS[kind]) {
					void client.invalidateQueries({ queryKey: [affected] });
				}
			}
		},
	});
}

export type ReachSource = "profile" | "individual" | "extra";

/**
 * O ACESSO FINAL de uma pessoa, com UMA origem por pedaço — calculado pelo
 * SERVIDOR com as mesmas funções que aplicam o acesso, para a tela nunca discordar
 * do que vale. `?profileId=` pré-visualiza outro perfil sem gravar.
 */
export interface EffectiveAccess {
	readonly role: Role;
	/** Admin e dono: veem e fazem tudo. */
	readonly unrestricted: boolean;
	readonly profile: { id: string; name: string } | null;
	readonly reach: Reach;
	readonly kinds: ReadonlyArray<{ kind: ResourceKind; open: boolean; source: ReachSource }>;
	readonly grants: ReadonlyArray<AccessGrant & { source: ReachSource }>;
	readonly seesAll: ReadonlyArray<{ entity: string; source: ReachSource }>;
	readonly modules?: ReadonlyArray<{
		module: AccessModule;
		enabled: boolean;
		source: ReachSource | "role" | null;
		adminOnly: boolean;
	}>;
	readonly ben?: { enabled: boolean; source: ReachSource | "role" | null };
}

/** O efetivo guardado de uma pessoa, ou — com `profileId` (`none` = sem perfil) — o pré-visualizado. */
export function fetchEffective(
	userId: string,
	profileId?: string | null,
): Promise<EffectiveAccess> {
	const query =
		profileId === undefined ? "" : `?profileId=${encodeURIComponent(profileId ?? "none")}`;
	return request(`/v3/identity/access/user/${encodeURIComponent(userId)}/effective${query}`);
}

/**
 * TUDO o que se pode conceder no workspace, sem o recorte de quem pergunta, com
 * `grantable` = quem pergunta pode dar. As listas de cada tela vêm recortadas pelo
 * alcance do editor — desenhar a matriz sobre elas fazia sumir o que ele tirava (01/11).
 */
export function useAccessCatalogue() {
	return useQuery({
		queryKey: ["access", "catalogue"] as const,
		queryFn: async () =>
			(
				await request<{
					items: Array<{
						kind: ResourceKind;
						key: string;
						name: string;
						hint: string | null;
						grantable: boolean;
						minRole?: { read?: "member" | "admin" | "owner"; write?: "member" | "admin" | "owner" };
					}>;
				}>("/v3/identity/access/catalogue")
			).items,
	});
}

/** Perfis de acesso: criar, renomear, apagar — tudo invalida o diretório inteiro. */
export function useProfileMutations() {
	const client = useQueryClient();
	const refresh = () => {
		void client.invalidateQueries({ queryKey: accessKeys.all() });
	};
	// Perfil e exceção mudam o que a pessoa alcança: as listas que dependem disso também.
	const reached = () => {
		refresh();
		for (const key of ["structure", "records", "inbox", "conversations", "board"])
			void client.invalidateQueries({ queryKey: [key] });
	};
	return {
		create: useMutation({
			mutationFn: (name: string) =>
				request<{ profile: AccessProfile }>("/v3/identity/access/profiles", {
					method: "POST",
					body: { name },
				}),
			onSuccess: refresh,
		}),
		rename: useMutation({
			mutationFn: (input: { id: string; name: string }) =>
				request(`/v3/identity/access/profiles/${encodeURIComponent(input.id)}`, {
					method: "PATCH",
					body: { name: input.name },
				}),
			onSuccess: refresh,
		}),
		remove: useMutation({
			mutationFn: (id: string) =>
				request(`/v3/identity/access/profiles/${encodeURIComponent(id)}`, { method: "DELETE" }),
			onSuccess: refresh,
		}),
		/** Dá (ou tira, com `null`) o perfil de uma pessoa. */
		assign: useMutation({
			mutationFn: (input: { userId: string; profileId: string | null }) =>
				request(`/v3/identity/access/user/${encodeURIComponent(input.userId)}/profile`, {
					method: "PUT",
					body: { profileId: input.profileId },
				}),
			onSuccess: reached,
		}),
		/** As tabelas em que esta pessoa, perfil ou extra vê todos os registros. */
		seesAll: useMutation({
			mutationFn: (input: {
				holderKind: "user" | "profile" | "extra";
				holderId: string;
				entities: string[];
			}) =>
				request(
					`/v3/identity/access/${input.holderKind}/${encodeURIComponent(input.holderId)}/sees-all`,
					{ method: "PUT", body: { entities: input.entities } },
				),
			onSuccess: reached,
		}),
		/** Os módulos de um perfil por inteiro: o que desliga e se dá o Ben. */
		modules: useMutation({
			mutationFn: (input: { id: string; disabledModules: AccessModule[]; benPanel: boolean }) =>
				request(`/v3/identity/access/profiles/${encodeURIComponent(input.id)}/modules`, {
					method: "PUT",
					body: { disabledModules: input.disabledModules, benPanel: input.benPanel },
				}),
			onSuccess: reached,
		}),
		/** Os módulos ligados só para esta pessoa além do perfil. */
		extraModules: useMutation({
			mutationFn: (input: { userId: string; modules: AccessModule[]; benPanel: boolean }) =>
				request(`/v3/identity/access/user/${encodeURIComponent(input.userId)}/extra-modules`, {
					method: "PUT",
					body: { modules: input.modules, benPanel: input.benPanel },
				}),
			onSuccess: reached,
		}),
	};
}

/** O interruptor do Ben do painel, por pessoa. */
export function useSetBenPanel() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (input: { userId: string; enabled: boolean }): Promise<boolean> => {
			const res = await request<{ enabled: boolean }>(
				`/v3/identity/access/user/${encodeURIComponent(input.userId)}/ben-panel`,
				{ method: "PUT", body: { enabled: input.enabled } },
			);
			return res.enabled;
		},
		onSuccess: () => {
			void client.invalidateQueries({ queryKey: accessKeys.all() });
			// E a própria capacidade: um admin que desliga o SEU Ben tem de o ver
			// desaparecer, e não só na próxima recarga.
			void client.invalidateQueries({ queryKey: ["access", "me"] });
		},
	});
}

/** O papel de um agente — a coluna que substituiu o pseudo-papel "workspace". */
export function useSetAgentRole() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (input: { code: string; role: Role }): Promise<Role> => {
			const res = await request<{ role: Role }>(
				`/v3/identity/access/agent/${encodeURIComponent(input.code)}/role`,
				{ method: "PUT", body: { role: input.role } },
			);
			return res.role;
		},
		onSuccess: () => {
			void client.invalidateQueries({ queryKey: accessKeys.all() });
			void client.invalidateQueries({ queryKey: ["agents"] });
		},
	});
}

// ── A REGRA, DO LADO DO DESENHO ────────────────────────────────────────────
//
// As mesmas três frases que o kernel do servidor diz, para a tela não ter de
// adivinhar nenhuma delas. Não é uma segunda implementação da decisão: é a
// LEITURA de um alcance que o servidor já devolveu, para desenhar caixas marcadas.

/** Este ator está estreitado neste tipo? Falso = alcança tudo o que houver dele. */
export function narrowedOn(reach: Reach, kind: ResourceKind): boolean {
	return reach.narrowed.includes(kind);
}

/**
 * Este ator alcança este recurso com este verbo?
 *
 * `write` satisfaz também o `read`, pela mesma razão do servidor: alterar sem
 * consultar não existe como operação.
 */
export function reaches(reach: Reach, kind: ResourceKind, key: string, verb: Verb): boolean {
	if (!narrowedOn(reach, kind)) return true;
	return reach.grants.some(
		(g) => g.kind === kind && g.key === key && (g.verb === verb || g.verb === "write"),
	);
}

/**
 * O alcance com UM recurso mudado — a caixa que se marca na matriz.
 *
 * Marcar "alterar" marca "consultar" junto, e desmarcar "consultar" desmarca
 * "alterar": é a regra que o DTO do servidor recusa violar, aplicada onde a pessoa
 * está a olhar, em vez de um 400 depois de gravar.
 *
 * Marcar o primeiro recurso de um tipo ESTREITA esse tipo — é o que faz uma
 * concessão querer dizer alguma coisa. Desmarcar o último REABRE, porque
 * "estreitado a nada" e "aberto" têm de continuar a ser dizíveis os dois: para
 * fechar um tipo inteiro existe `closeKind`.
 */
export function withGrant(
	reach: Reach,
	kind: ResourceKind,
	key: string,
	verb: Verb,
	on: boolean,
): Reach {
	const others = reach.grants.filter((g) => !(g.kind === kind && g.key === key));
	const mine = reach.grants.filter((g) => g.kind === kind && g.key === key);
	const hasRead = on ? true : verb === "write" && mine.some((g) => g.verb === "read");
	const hasWrite = verb === "write" ? on : on && mine.some((g) => g.verb === "write");

	const next: AccessGrant[] = [...others];
	if (hasRead) next.push({ kind, key, verb: "read" });
	if (hasWrite) next.push({ kind, key, verb: "write" });

	const stillHere = next.some((g) => g.kind === kind);
	const narrowed = stillHere
		? [...new Set([...reach.narrowed, kind])]
		: reach.narrowed.filter((k) => k !== kind);
	return { narrowed, grants: next };
}

/**
 * VÁRIOS RECURSOS DE UMA VEZ, a partir do que a pessoa VÊ marcado (20/10).
 *
 * A matriz mostra sempre a lista, com cada item como ele está de fato: um tipo
 * aberto aparece todo marcado. Desmarcar um item de um tipo aberto estreita o
 * tipo com todos os OUTROS ainda marcados — antes era preciso "Alcançar nada" e
 * remarcar um por um. Tudo marcado (consultar e alterar) volta a ser "alcança
 * tudo", o que inclui o que for criado depois; nada marcado é "nada".
 */
export function withResources(
	reach: Reach,
	kind: ResourceKind,
	all: readonly string[],
	keys: readonly string[],
	verb: Verb,
	on: boolean,
	/** Nunca reabre o tipo (os extras só concedem item a item). */
	keepNarrowed = false,
): Reach {
	// 0 nada, 1 consultar, 2 alterar (que inclui consultar).
	const level = new Map(
		all.map((k) => [
			k,
			reaches(reach, kind, k, "write") ? 2 : reaches(reach, kind, k, "read") ? 1 : 0,
		]),
	);
	for (const key of keys) {
		const now = level.get(key) ?? 0;
		level.set(key, verb === "write" ? (on ? 2 : Math.min(now, 1)) : on ? Math.max(now, 1) : 0);
	}
	const others = reach.grants.filter((g) => g.kind !== kind);
	if (!keepNarrowed && all.length > 0 && [...level.values()].every((v) => v === 2)) {
		return { narrowed: reach.narrowed.filter((k) => k !== kind), grants: others };
	}
	const mine: AccessGrant[] = [...level].flatMap(([key, v]): AccessGrant[] =>
		v === 0
			? []
			: v === 1
				? [{ kind, key, verb: "read" }]
				: [
						{ kind, key, verb: "read" },
						{ kind, key, verb: "write" },
					],
	);
	return { narrowed: [...new Set([...reach.narrowed, kind])], grants: [...others, ...mine] };
}

/** Fecha um tipo inteiro: estreitado e sem concessão nenhuma — alcança NADA dele. */
export function closeKind(reach: Reach, kind: ResourceKind): Reach {
	return {
		narrowed: [...new Set([...reach.narrowed, kind])],
		grants: reach.grants.filter((g) => g.kind !== kind),
	};
}

/** Reabre um tipo inteiro: sem estreitamento — alcança TUDO dele. */
export function openKind(reach: Reach, kind: ResourceKind): Reach {
	return {
		narrowed: reach.narrowed.filter((k) => k !== kind),
		grants: reach.grants.filter((g) => g.kind !== kind),
	};
}

export const OPEN_REACH: Reach = { narrowed: [], grants: [] };

export function useSetModules() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (input: { userId: string; modules: readonly AccessModule[]; enabled: boolean }) =>
			request(`/v3/identity/access/user/${encodeURIComponent(input.userId)}/modules`, {
				method: "PUT",
				body: { modules: input.modules, enabled: input.enabled },
			}),
		onSuccess: () => client.invalidateQueries({ queryKey: accessKeys.all() }),
	});
}
